import React, { useState, useEffect } from 'react';
import UpcomingEvents from './UpcomingEvents';
import { Card, Container, Row, Col, Button, Alert, Form, Tabs, Tab, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from '../../services/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import 'bootstrap-icons/font/bootstrap-icons.css'; // Aggiungo le icone Bootstrap
import { formatDate, formatDateRange } from '../../utils/dateUtils';

const AssociationDashboard = () => {
  const [events, setEvents] = useState([]);
  // Partecipanti rimossi su richiesta
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for payment requests
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsError, setRequestsError] = useState(null);
  
  const [activeTab, setActiveTab] = useState('events');
  const [filters, setFilters] = useState({
    category: '',
    sda: '',
    date: '',
    searchTerm: '',
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchPaymentRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchPaymentRequests = async () => {
    if (!user || !user.association_id) {
      setPaymentRequests([]);
      return;
    }
    
    try {
      setLoadingRequests(true);
      setRequestsError(null);
      const response = await axios.get(`/api/payments/requests/association/${user.association_id}`);
      setPaymentRequests(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error fetching payment requests:', err);
      setRequestsError('Impossibile caricare le richieste di pagamento. Riprova più tardi.');
    } finally {
      setLoadingRequests(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      // Build query params for filtering
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.sda) params.append('sda', filters.sda);
      if (filters.date) params.append('date', filters.date);

      // Get events based on association's region
      const response = await axios.get(`/api/events?${params.toString()}`);
      // Verifico che response.data.events esista, altrimenti utilizzo response.data o un array vuoto
      let allEvents = response.data.events || response.data || [];

      // Preprocess events to ensure image data is properly handled
      allEvents = allEvents.map(event => {
        // Handle potential image data issues
        if (event.image) {
          // Make sure image data is properly formatted
          if (typeof event.image === 'string') {
            try {
              // Check if it's already a valid base64 string
              if (event.image.startsWith('data:image')) {
                // It's already a data URL, keep as is
                const parts = event.image.split(',');
                if (parts.length === 2) {
                  const contentType = parts[0].split(':')[1].split(';')[0];
                  event.image = {
                    data: parts[1],
                    contentType: contentType
                  };
                }
              } else {
                // Try parsing as JSON
                event.image = JSON.parse(event.image);
              }
            } catch (e) {
              console.error('Error processing image data:', e);
              // If parsing fails, create a structured object but mark as invalid
              // to prevent repeated fetch attempts
              event.image = null;
            }
          }

          // Validate image data
          if (event.image && (!event.image.data || event.image.data === '')) {
            console.log('Empty image data detected, setting to null:', event.title || event._id);
            event.image = null;
          }

          // Ensure contentType exists
          if (event.image && !event.image.contentType && event.image.data) {
            event.image.contentType = 'image/jpeg';  // Default to jpeg if not specified
          }
        }
        return event;
      });

      // Applico filtri lato client se necessario
      // Questo è utile nel caso in cui l'API non supporti tutti i tipi di filtro
      if (filters.searchTerm && filters.searchTerm.trim() !== '') {
        const searchTerm = filters.searchTerm.toLowerCase();
        allEvents = allEvents.filter(event =>
          (event.title && event.title.toLowerCase().includes(searchTerm)) ||
          (event.description && event.description.toLowerCase().includes(searchTerm)) ||
          (event.location && event.location.toLowerCase().includes(searchTerm)) ||
          (event.category && event.category.toLowerCase().includes(searchTerm))
        );
      }

      setEvents(allEvents);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setLoading(false);
    }
  };

  // fetchParticipants rimosso

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchEvents();
  };

  const resetFilters = () => {
    setFilters({
      category: '',
      sda: '',
      date: '',
      searchTerm: ''
    });
    // Fetch all events without filters
    fetchEvents();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'succeeded':
        return <Badge bg="success">Saldato</Badge>;
      case 'awaiting_payment':
      case 'pending':
        return <Badge bg="warning" text="dark">In attesa di pagamento</Badge>;
      case 'awaiting_verification':
        return <Badge bg="info">Bonifico in verifica</Badge>;
      case 'failed':
        return <Badge bg="danger">Fallito</Badge>;
      case 'cancelled':
        return <Badge bg="secondary">Annullato</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Custom styles for UISP theme (currently unused, kept for reference)
  // eslint-disable-next-line no-unused-vars
  const uispStyles = {
    button: {
      backgroundColor: "#006e39",
      borderColor: "#006e39",
      color: "white"
    },
    outlineButton: {
      backgroundColor: "transparent",
      borderColor: "#006e39",
      color: "#006e39"
    },
    card: {
      borderColor: "#e0e0e0",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)"
    },
    badge: {
      backgroundColor: "#006e39"
    },
    header: {
      color: "#006e39"
    },
    tab: {
      color: "#006e39"
    }
  };

  if (loading && activeTab === 'events') {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
        <p className="mt-3">Caricamento eventi...</p>
      </div>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <h1 style={{ color: "#006e39" }}>Dashboard Associazione</h1>
        <div>
          <Link to="/association/members">
            <Button
              variant="outline-success"
              style={{
                borderColor: "#006e39",
                color: "#006e39"
              }}
            >
              <i className="bi bi-search me-2"></i>
              I tuoi soci
            </Button>
          </Link>
        </div>
      </div>

      {/* Prossimi eventi in evidenza */}
      <UpcomingEvents limit={3} />

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="events" title="Eventi disponibili">
          {error && <Alert variant="danger">{error}</Alert>}
          <Card className="mb-4 border-0 shadow-sm mt-3">
            <Card.Header style={{ backgroundColor: "#006e39", color: "white" }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-funnel me-2"></i>
              Filtra eventi
            </h5>
            <Badge bg="light" text="dark">
              {events.length} eventi disponibili
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={applyFilters}>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label><i className="bi bi-search"></i> Cerca</Form.Label>
                  <div className="input-group input-group-lg">
                    <span className="input-group-text" style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                      <i className="bi bi-search" style={{ color: '#006e39' }}></i>
                    </span>
                    <Form.Control
                      type="text"
                      name="searchTerm"
                      value={filters.searchTerm}
                      onChange={handleFilterChange}
                      placeholder="Cerca eventi per titolo, descrizione, luogo..."
                      style={{ borderColor: '#006e39' }}
                    />
                    {filters.searchTerm && (
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => setFilters(prev => ({ ...prev, searchTerm: '' }))}
                        title="Cancella ricerca"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label><i className="bi bi-tag"></i> Categoria</Form.Label>
                  <Form.Select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                  >
                    <option value="">Tutte le categorie</option>
                    <option value="manifestazione">Manifestazione</option>
                    <option value="stage">Stage</option>
                    <option value="acquaviva">Acquaviva</option>
                    <option value="atletica leggera">Atletica Leggera</option>
                    <option value="calcio">Calcio</option>
                    <option value="ciclismo">Ciclismo</option>
                    <option value="danza">Danza</option>
                    <option value="discipline orientali">Discipline Orientali</option>
                    <option value="equestri e cinofile">Equestri e Cinofile</option>
                    <option value="ginnastiche">Ginnastiche</option>
                    <option value="giochi">Giochi</option>
                    <option value="montagna">Montagna</option>
                    <option value="motorismo">Motorismo</option>
                    <option value="neve">Neve</option>
                    <option value="nuoto">Nuoto</option>
                    <option value="pallacanestro">Pallacanestro</option>
                    <option value="pallavolo">Pallavolo</option>
                    <option value="pattinaggio">Pattinaggio</option>
                    <option value="subacquea">Subacquea</option>
                    <option value="tennis">Tennis</option>
                    <option value="vela">Vela</option>
                    <option value="multi-sport">Multi-Sport</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col sm={6} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label><i className="bi bi-diagram-2"></i> SDA</Form.Label>
                  <Form.Control
                    type="text"
                    name="sda"
                    value={filters.sda}
                    onChange={handleFilterChange}
                    placeholder="Disciplina sportiva"
                  />
                </Form.Group>
              </Col>
              <Col sm={6} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label><i className="bi bi-calendar"></i> Data</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={filters.date}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
              <Col md={12} className="d-flex justify-content-end">
                <div className="mb-3 d-flex flex-wrap gap-2">
                  <Button type="submit" style={{ backgroundColor: "#006e39", borderColor: "#006e39", color: "white" }}>
                    <i className="bi bi-filter"></i> Applica filtri
                  </Button>
                  <Button variant="secondary" onClick={resetFilters}>
                    <i className="bi bi-x-circle"></i> Reset
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <h2 className="mb-3">Eventi disponibili</h2>

      {events.length === 0 ? (
        <Alert variant="info">
          <i className="bi bi-info-circle me-2"></i>
          Nessun evento trovato con i criteri selezionati.
        </Alert>
      ) : (
        <Row>
          {events.map(event => {
            // Calculate registration status including late registrations
            const now = new Date();
            const eventDate = new Date(event.eventDate || event.event_date);
            const registrationDeadline = event.registrationDeadline
              ? new Date(event.registrationDeadline)
              : (event.registration_deadline ? new Date(event.registration_deadline) : null);
            const allowLateRegistration = event.allowLateRegistration === true || event.allow_late_registration === true;

            const isEventStarted = !isNaN(eventDate.getTime()) && now > eventDate;
            const isBeforeDeadline = registrationDeadline ? now <= registrationDeadline : true;
            const isLateRegistrationOpen = !isBeforeDeadline && allowLateRegistration && !isEventStarted;
            const isRegistrationOpen = !isEventStarted && (isBeforeDeadline || isLateRegistrationOpen);

            const badgeClass = isLateRegistrationOpen ? 'bg-warning text-dark' : (isRegistrationOpen ? 'bg-success' : 'bg-danger');
            const badgeLabel = isLateRegistrationOpen ? 'Tardive' : (isRegistrationOpen ? 'Aperte' : 'Chiuse');
            const badgeIcon = isLateRegistrationOpen
              ? 'bi-exclamation-triangle-fill'
              : (isRegistrationOpen ? 'bi-check-circle-fill' : 'bi-x-circle-fill');
            
            return (
            <Col xs={12} sm={6} md={4} key={event._id || event.id} className="mb-4">
              <Card 
                className="h-100 shadow-sm border-0" 
                style={{ 
                  borderRadius: '12px',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{
                  height: '180px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Registration Status Badge */}
                  <span 
                    className={`badge position-absolute ${badgeClass}`}
                    style={{ 
                      top: '10px', 
                      right: '10px', 
                      zIndex: 10,
                      fontSize: '0.85rem',
                      padding: '8px 12px'
                    }}
                  >
                    <i className={`bi ${badgeIcon} me-1`}></i>
                    {badgeLabel}
                  </span>
                  
                  {(event.image_url || (event.image && event.image.data)) ? (
                    <Card.Img
                      variant="top"
                      src={
                        event.image_url
                        || (event.image && event.image.contentType && event.image.data
                          ? `data:${event.image.contentType};base64,${event.image.data}`
                          : null)
                      }
                      alt={event.title || "Immagine evento"}
                      style={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        // Solo log una volta
                        console.error("Immagine non caricata per l'evento:", event.title || event._id);
                        e.target.style.display = 'none';
                        const parent = e.target.parentElement;
                        if (parent) {
                          parent.innerHTML = `<div style="
                            height: 100%;
                            width: 100%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            background-color: #f8f9fa;
                          "><i class="bi bi-calendar-event" style="font-size: 3rem; color: #006e39;"></i></div>`;
                        }
                      }}
                    />
                  ) : (
                    <div style={{
                      height: '100%',
                      backgroundColor: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <i className="bi bi-calendar-event" style={{ fontSize: '3rem', color: '#006e39' }}></i>
                    </div>
                  )}
                </div>
                <Card.Body>
                  <Card.Title>{event.title}</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    {formatDateRange(event.eventDate || event.event_date, event.eventEndDate || event.event_end_date || event.eventDate || event.event_date)}
                    {(event.startTime || event.start_time) && ` - ${event.startTime || event.start_time}`}
                  </Card.Subtitle>
                  <div className="mb-2 d-flex gap-2">
                    {event.scope && (<Badge style={{
                      backgroundColor:
                        event.scope === 'national' ? '#dc3545' :
                          event.scope === 'regional' ? '#e6a23c' :
                            '#006e39',
                      fontSize: '0.9rem'
                    }}>
                      {event.scope === 'national' ? 'Nazionale' : 
                       event.scope === 'regional' ? 'Regionale' : 
                       event.scope === 'territorial' ? 'Territoriale' : event.scope}
                    </Badge>
                    )}
                    {event.category && (
                      <Badge style={{ backgroundColor: "#006e39", fontSize: '0.9rem' }}>{event.category}</Badge>
                    )}
                    {event.sda && (
                      <Badge style={{ backgroundColor: "#006e39", fontSize: '0.9rem' }}>{event.sda}</Badge>
                    )}
                  </div>
                  <Card.Text>
                    {event.description && event.description.length > 100
                      ? `${event.description.substring(0, 100)}...`
                      : event.description}
                  </Card.Text>
                  <div className="mt-3">
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">
                        <i className="bi bi-geo-alt-fill text-danger me-1"></i> {event.location || 'Località non specificata'}
                      </small>
                    </div>
                    {/* {event.price !== undefined && (
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">
                          <i className="bi bi-currency-euro text-warning me-1"></i> {event.price === 0 ? 'Gratuito' : `€${event.price}`}
                        </small>
                      </div>
                    )} */}
                  </div>
                </Card.Body>
                <Card.Footer className="d-flex justify-content-end gap-2">
                  <Link to={`/association/events/${event._id}`}>
                    <Button style={{ backgroundColor: "#006e39", borderColor: "#006e39", color: "white" }} size="sm">
                      <i className="bi bi-eye"></i> Dettagli
                    </Button>
                  </Link>
                  {isRegistrationOpen && event.registrationMode === 'team' && (
                    <Link to={`/association/events/${event._id}/team-registration`}>
                      <Button variant="outline-success" size="sm">
                        <i className="bi bi-people"></i> Iscrivi squadra
                      </Button>
                    </Link>
                  )}
                  {isRegistrationOpen && event.registrationMode === 'individual' && (
                    <Link to={`/association/events/${event._id}/select-members`}>
                      <Button variant="outline-primary" size="sm">
                        <i className="bi bi-person-plus"></i> Seleziona soci individuali
                      </Button>
                    </Link>
                  )}
                </Card.Footer>
              </Card>
            </Col>
          );
          })}
        </Row>
      )}
        </Tab>
        <Tab eventKey="requests" title="Richieste di pagamento">
          {loadingRequests ? (
            <div className="text-center mt-5">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Caricamento...</span>
              </div>
              <p className="mt-3">Caricamento richieste...</p>
            </div>
          ) : requestsError ? (
            <Alert variant="danger">{requestsError}</Alert>
          ) : paymentRequests.length === 0 ? (
            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              Nessuna richiesta di pagamento trovata.
            </Alert>
          ) : (
            <Row className="mt-3">
              {paymentRequests.map((req) => (
                <Col xs={12} sm={6} md={4} key={req.id || req._id} className="mb-4">
                  <Card className="h-100 shadow-sm border-0" style={{ borderRadius: '12px' }}>
                    <Card.Body>
                      <Card.Title>Richiesta di Pagamento</Card.Title>
                      <Card.Subtitle className="mb-3 text-muted">
                        Data: {formatDate(req.created_at || req.createdAt)}
                      </Card.Subtitle>
                      
                      <div className="mb-2">
                        {req.events && req.events.length > 0 ? (
                            req.events.map(ev => (
                                <div key={ev.id || ev._id}><i className="bi bi-calendar-event me-2 text-primary"></i> <strong>{ev.title || `Evento #${ev.id || ev._id}`}</strong></div>
                            ))
                        ) : req.event ? (
                            <div><i className="bi bi-calendar-event me-2 text-primary"></i> <strong>{req.event.title || `Evento #${req.event_id || req.eventId}`}</strong></div>
                        ) : (
                            <div><i className="bi bi-calendar-event me-2 text-primary"></i> <strong>Evento #{req.event_id || req.eventId}</strong></div>
                        )}
                      </div>
                      
                      <div className="mb-2">
                        <i className="bi bi-currency-euro me-2 text-warning"></i>
                        <strong>Importo:</strong> €{((req.amount || 0) / 100).toFixed(2)}
                      </div>
                      
                      <div className="mb-2">
                        <strong>Stato:</strong> {getStatusBadge(req.status)}
                      </div>
                    </Card.Body>
                    <Card.Footer className="bg-white border-top-0 d-flex justify-content-end pb-3">
                      <Link to={`/association/events/${req.event_id || req.eventId || (req.events && req.events[0] ? (req.events[0].id || req.events[0]._id) : '')}/payment?request=${req.id}`}>
                        <Button 
                          variant={req.status === 'succeeded' ? "outline-secondary" : "success"}
                          size="sm"
                          style={req.status !== 'succeeded' ? { backgroundColor: "#006e39", borderColor: "#006e39" } : {}}
                        >
                          {req.status === 'succeeded' ? (
                            <><i className="bi bi-eye me-1"></i> Visualizza Ricevuta</>
                          ) : (
                            <><i className="bi bi-credit-card me-1"></i> Procedi al pagamento / Visualizza</>
                          )}
                        </Button>
                      </Link>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default AssociationDashboard;